import { z } from 'zod';
import { ChinaRequestsPeriodKeySchema } from '../dashboards/chinaRequests.js';

// WHY: filters and values are open records — their keys depend on the metric
//   and the SQL query; they cannot be known statically. The open shape is
//   intentional per spec.

export const AgentNoDataReasonSchema = z.enum([
  'unsupported_domain',
  'missing_schema',
  'ambiguous_period',
  'unsafe_columns',
  'too_broad',
  'query_rejected',
  'query_timeout',
  'missing_figure',
  'non_numeric_figure',
  'too_many_tasks',
  'too_many_rows',
]);

/**
 * @aiPurpose provenance block attached to an agent verdict, identifying
 *   which data source produced each numeric answer and what parameters were
 *   used. Allows the UI to render a "how did we get this?" trail and lets
 *   the reconciliation loop detect genuine discrepancies vs. parameter drift.
 * @aiInvariant `source` is either `'registry'` (pre-aggregated metric store)
 *   or `'sql'` (ad-hoc sandbox query). `values` always has at least one
 *   numeric entry. `sql` is populated only when `source === 'sql'`.
 *   DB-first task metadata is optional so v1 provenance remains valid.
 */
export const ProvenanceSchema = z
  .object({
    source: z.enum(['registry', 'sql']),
    metric: z.string().optional(),
    sql: z.string().optional(),
    taskId: z.string().optional(),
    taskLabel: z.string().optional(),
    figureKeys: z.array(z.string()).optional(),
    noDataReason: AgentNoDataReasonSchema.optional(),
    period: ChinaRequestsPeriodKeySchema.exclude(['custom']).optional(),
    filters: z.record(z.union([z.string(), z.number()])).optional(),
    values: z.record(z.number()),
  })
  .strict();

/**
 * @aiPurpose purpose-built SQL-free chart-card provenance: only the safe
 *   registry/source label plus metric/period/no-data reason.
 * @aiSecurity (#2006, epic #2004) charts are broadcast to every `dashboard.read`
 *   chat user, so this schema deliberately does NOT inherit `ProvenanceSchema`'s
 *   raw `sql`, open `filters`/`values` records, `figureKeys`, or task
 *   identifiers — none of those can ever appear on the broadcast wire. `.strict()`
 *   rejects any of those keys as unknown.
 */
export const ChartProvenanceSchema = z
  .object({
    source: z.enum(['registry', 'sql']),
    metric: z.string().optional(),
    period: ChinaRequestsPeriodKeySchema.exclude(['custom']).optional(),
    noDataReason: AgentNoDataReasonSchema.optional(),
  })
  .strict();

export type Provenance = z.infer<typeof ProvenanceSchema>;
export type ChartProvenance = z.infer<typeof ChartProvenanceSchema>;
export type AgentNoDataReason = z.infer<typeof AgentNoDataReasonSchema>;
